/**
 * Страница для клиента.
 *
 * Открывается по ссылке без входа в систему. Заказчик видит только свои
 * готовые работы: ни цен, ни исполнителей, ни чего-либо внутреннего.
 *
 * Сделано намеренно просто: человек пришёл забрать файлы, а не изучать
 * интерфейс. Крупные превью, понятные кнопки, никаких меню.
 */
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api';
import { bytes, duration } from '../lib/format';

type ShareItem = {
  index: number;
  durationSec: number | null;
  width: number | null;
  height: number | null;
  final: { id: string; name: string; sizeBytes: string } | null;
  cover: { id: string; name: string; sizeBytes: string } | null;
  /** Готовая ссылка на превью — приходит вместе со списком, отдельный запрос не нужен */
  previewUrl: string | null;
};

type ShareContent = {
  title: string | null;
  expiresAt: string | null;
  videos: ShareItem[];
};

export function SharePage() {
  const { token } = useParams<{ token: string }>();
  const [params, setParams] = useSearchParams();
  const password = params.get('p') ?? '';
  const [passInput, setPassInput] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['share', token, password],
    queryFn: () =>
      api.get<ShareContent>(
        `/api/share/${token}${password ? `?password=${encodeURIComponent(password)}` : ''}`,
      ),
    enabled: Boolean(token),
    retry: false,
  });

  const download = async (fileId: string) => {
    const res = await api.get<{ url: string }>(
      `/api/share/${token}/file/${fileId}${password ? `?password=${encodeURIComponent(password)}` : ''}`,
    );
    window.location.href = res.url;
  };

  // Нужен пароль — показываем поле вместо ошибки
  if (error instanceof ApiError && error.status === 403) {
    return (
      <Shell>
        <div className="card max-w-sm mx-auto">
          <div className="font-bold mb-1">Ссылка защищена паролем</div>
          <div className="text-[12.5px] text-muted2 mb-3">Введи пароль, который тебе передали.</div>
          <input
            className="input mb-2"
            type="password"
            value={passInput}
            onChange={(e) => setPassInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setParams({ p: passInput })}
            autoFocus
          />
          <button className="btn btn-primary w-full" onClick={() => setParams({ p: passInput })}>
            Открыть
          </button>
        </div>
      </Shell>
    );
  }

  /**
   * Слишком частые запросы — это не «ссылка умерла».
   *
   * Раньше любая ошибка показывалась как недействительная ссылка, и
   * заказчик шёл ругаться, хотя достаточно было подождать минуту.
   * Теперь причина названа честно, и есть кнопка повтора.
   */
  if (error instanceof ApiError && error.status === 429) {
    return (
      <Shell>
        <div className="text-center py-16">
          <div className="text-4xl opacity-30 mb-4">◔</div>
          <div className="text-lg font-bold mb-2">Слишком много запросов</div>
          <div className="text-[13px] text-muted2 leading-relaxed max-w-xs mx-auto mb-4">
            Со ссылкой всё в порядке. Подожди минуту и обнови страницу —
            это ограничение от роботов, оно снимается само.
          </div>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Обновить
          </button>
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <div className="text-center py-16">
          <div className="text-4xl opacity-30 mb-4">◎</div>
          <div className="text-lg font-bold mb-2">Ссылка недоступна</div>
          <div className="text-[13px] text-muted2 leading-relaxed max-w-xs mx-auto">
            Возможно, срок её действия истёк или её отозвали.
            Попроси новую у того, кто её прислал.
          </div>
        </div>
      </Shell>
    );
  }

  if (isLoading || !data) {
    return (
      <Shell>
        <div className="space-y-3 animate-pulse">
          {[0, 1].map((i) => (
            <div key={i} className="h-48 rounded-xl2 bg-surface border border-line" />
          ))}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold tracking-[-0.02em]">{data.title}</h1>
        <div className="text-[13px] text-muted2 mt-1.5">
          Готовых работ: {data.videos.length}
          {data.expiresAt &&
            ` · ссылка действует до ${new Date(data.expiresAt).toLocaleDateString('ru-RU')}`}
        </div>
      </div>

      {data.videos.length === 0 ? (
        <div className="card text-center py-12 text-muted2">
          Работы ещё не готовы. Загляни позже.
        </div>
      ) : (
        <div className="space-y-3">
          {data.videos.map((v) => (
            <div key={v.index} className="card">
              <div className="flex flex-col sm:flex-row gap-4">
                <Preview url={v.previewUrl} />

                <div className="flex-1 min-w-0">
                  <div className="font-bold">Видео {v.index}</div>
                  <div className="text-[12px] text-muted2 mt-1">
                    {v.durationSec ? duration(v.durationSec) : ''}
                    {v.width ? ` · ${v.width}×${v.height}` : ''}
                    {v.final ? ` · ${bytes(v.final.sizeBytes)}` : ''}
                  </div>

                  <div className="flex gap-2 mt-3 flex-wrap">
                    {v.final && (
                      <button className="btn btn-primary" onClick={() => void download(v.final!.id)}>
                        ⤓ Скачать видео
                      </button>
                    )}
                    {v.cover && (
                      <button className="btn" onClick={() => void download(v.cover!.id)}>
                        ⤓ Обложка
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-center text-[11.5px] text-muted2 mt-8 leading-relaxed">
        Скачивание идёт напрямую с сервера, на полной скорости.
        <br />
        Большие файлы лучше качать при стабильном соединении.
      </div>
    </Shell>
  );
}

/**
 * Превью: обложка, если её сделали, иначе кадр из видео.
 *
 * Ссылка приходит вместе со списком работ. Раньше здесь был отдельный
 * запрос на каждое видео — при пятнадцати работах страница делала
 * шестнадцать обращений за одно открытие и упиралась в ограничение
 * частоты, показывая заказчику «ссылка недоступна».
 */
function Preview({ url }: { url: string | null }) {
  if (!url) {
    return <div className="w-full sm:w-40 aspect-video rounded-lg bg-surface2 shrink-0" />;
  }

  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      className="w-full sm:w-40 aspect-video object-cover rounded-lg border border-line shrink-0"
    />
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="text-[24px] tracking-[0.01em] font-extrabold text-accent2 mb-8">
          Конторка
        </div>
        {children}
      </div>
    </div>
  );
}
